import {
  Cell,
  CellContext,
  ColumnDef,
  ColumnFiltersState,
  HeaderContext,
  OnChangeFn,
  PaginationState,
  Row,
  SortingState,
  Table as TableModel,
  flexRender,
} from '@tanstack/react-table';
import { Checkbox, Skeleton } from 'antd';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import './Table.scss';
import { TableHeaderRows } from './TableHeaderRows';
import { TablePagination } from './TablePagination';
import type { RowData, WithOptionalId } from './table-types';
import { useTableModel } from './useTableModel';

// TODO: enforce row IDs

export type { Table as TableModel } from '@tanstack/react-table';

export type { RowData };

export type ColumnType<T extends RowData> = ColumnDef<T>;

/**
 * returns JSX for empty state
 */
const emptyStateElement = (
  <div className="table-empty-state">
    <h4>No matching records found</h4>
  </div>
);

/**
 * Handle checkbox checked unchecked event of header in table
 */
export function HeaderCheckBox<T extends RowData>({
  table,
}: HeaderContext<T, unknown>) {
  return (
    <Checkbox
      checked={table.getIsAllRowsSelected()}
      indeterminate={table.getIsSomeRowsSelected()}
      onChange={table.getToggleAllRowsSelectedHandler()}
      aria-label="Select all rows"
    />
  );
}

/**
 * Handle the checkbox checked unchecked event of individual row in table
 */
export function CellCheckBox<T extends RowData>({
  row,
}: CellContext<T, unknown>) {
  return (
    <Checkbox
      checked={row.getIsSelected()}
      disabled={!row.getCanSelect()}
      onChange={row.getToggleSelectedHandler()}
      aria-label="Select row"
    />
  );
}

/**
 * Define type of Table's Props
 */
export interface TableProps<T extends RowData> {
  data: T[];
  columns: ColumnType<T>[];
  isLoading: boolean;
  globalFilter?: string;
  manualSorting?: boolean;
  manualFiltering?: boolean;
  manualPagination?: boolean;
  totalEntries?: number;
  initialSorting?: SortingState;
  selectedRow?: T[];
  setSelectedRow?: (t: Row<T>[]) => void;
  changeActions?: {
    onSortingChange?: OnChangeFn<SortingState>;
    onPaginationChange?: OnChangeFn<PaginationState>;
    onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
  };
  manualState?: {
    sorting?: SortingState;
    pagination?: PaginationState;
    columnFilters?: ColumnFiltersState;
  };
  highLightColumnsById?: string[];
  highlightColumnsCell?: boolean;
  rowEditable?: boolean;
  setUpdatedTableData?: (t: T[] | ((prev: T[]) => T[])) => void;
  error?: T extends WithOptionalId ? Pick<T, 'id'>[] : undefined;
  displayError?: boolean;
  // TODO: rework this clumsy error handling
  // Optional custom row validation function
  hasError?: T extends WithOptionalId
    ? (data: Pick<T, 'id'>[], idToCheck: T['id']) => boolean
    : undefined;
  // Optional custom cell class name function
  getCellClassName?: (cell: Cell<T, unknown>, row: Row<T>) => string;
  setTableModel?: (model: TableModel<T>) => void;
}

/**
 * returns JSX for table
 */
export function Table<T extends RowData>({
  data,
  isLoading = true,
  globalFilter = '',
  columns,
  manualSorting = false,
  manualFiltering = false,
  manualPagination = false,
  manualState = {},
  selectedRow: selectedRowProp,
  setSelectedRow,
  changeActions = {},
  initialSorting = [],
  highLightColumnsById = [],
  highlightColumnsCell = false,
  rowEditable = false,
  setUpdatedTableData,
  error,
  displayError,
  hasError,
  getCellClassName,
  setTableModel,
  ...p
}: TableProps<T>) {
  const selectedRow = useMemo(() => selectedRowProp ?? [], [selectedRowProp]);
  const hasSelectedRowProp = selectedRowProp !== undefined;
  const totalEntries = p.totalEntries ?? data.length;
  const [pagination, setPagination] = useState<PaginationState>({
    pageSize: 25,
    pageIndex: 0,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [rowSelection, setRowSelection] = useState({});
  const [tableData, setTableData] = useState<T[]>(data);

  // Track the last notified selection length to avoid infinite loops
  const lastNotifiedLengthRef = useRef<number>(0);

  /**
   * Handle row edit change
   */
  const handleRowEdit = (
    rowIndex: number,
    columnId: string,
    value: unknown,
  ) => {
    setTableData((prev) =>
      prev.map((row, index) =>
        index === rowIndex ? { ...row, [columnId]: value } : row,
      ),
    );
    if (setUpdatedTableData) {
      setUpdatedTableData((prev: T[]) =>
        prev.map((row, index) =>
          index === rowIndex ? { ...row, [columnId]: value } : row,
        ),
      );
    }
  };

  const defaultHasError = (data: T[], idToCheck?: string | number) => {
    return (
      data.some((item) => (item as WithOptionalId)['id'] === idToCheck) &&
      rowEditable &&
      displayError
    );
  };

  const errorChecker =
    (hasError as typeof defaultHasError | undefined) ?? defaultHasError;

  useLayoutEffect(() => {
    setTableData(data);
    if (setUpdatedTableData) {
      setUpdatedTableData(data);
    }
  }, [data, setUpdatedTableData]);

  const tableState = {
    columnFilters,
    columnVisibility,
    rowSelection,
    sorting,
    globalFilter,
    pagination,
    ...manualState,
  };
  const stateChanges = {
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
  };
  const table = useTableModel(
    tableData,
    columns,
    tableState,
    stateChanges,
    {
      manualFiltering,
      manualSorting,
      manualPagination,
    },
    changeActions,
  );

  useLayoutEffect(() => {
    setTableModel?.(table);
  }, [setTableModel, table]);

  useEffect(() => {
    // Only clear selection if selectedRow prop is explicitly provided and empty
    if (
      hasSelectedRowProp &&
      Object.keys(rowSelection).length &&
      !selectedRow.length
    ) {
      setRowSelection({});
    }
  }, [hasSelectedRowProp, selectedRow, rowSelection]);

  useEffect(() => {
    const newSelectedRow = table.getSelectedRowModel().flatRows;
    const newLength = newSelectedRow.length;

    // Only call setSelectedRow if the length actually changed to avoid infinite loops
    if (setSelectedRow && newLength !== lastNotifiedLengthRef.current) {
      lastNotifiedLengthRef.current = newLength;
      setSelectedRow(newSelectedRow);
    }
  }, [rowSelection, setSelectedRow, table]);

  return (
    <>
      <div className="table-wrapper">
        <table
          {...{
            className: 'table',
            style: {
              minWidth: table.getTotalSize(),
              width: '100%',
            },
          }}
        >
          <thead className="thead">
            <TableHeaderRows
              table={table}
              highLightColumnsById={highLightColumnsById}
            />
          </thead>
          <tbody
            {...{
              className: 'tbody',
            }}
          >
            {isLoading
              ? [...Array(pagination.pageSize).keys()].map((i) => (
                  <tr
                    {...{
                      className: 'tr',
                    }}
                    key={i}
                  >
                    {table.getLeafHeaders().map((header) => (
                      <td
                        {...{
                          key: header.id,
                          className: 'td',
                          style: {
                            position: 'relative',
                            minWidth: header.getSize(),
                          },
                        }}
                        key={header.id}
                      >
                        <Skeleton
                          loading
                          title={false}
                          paragraph={{ rows: 1 }}
                        />
                      </td>
                    ))}
                  </tr>
                ))
              : null}
            {table.getRowModel().rows.map((row) => (
              <tr
                {...{
                  className: `tr ${
                    (row.original as WithOptionalId).id &&
                    error &&
                    errorChecker(
                      error as T[],
                      (row.original as WithOptionalId).id,
                    )
                      ? 'error-class'
                      : ''
                  }`,
                  style: {
                    position: 'relative',
                  },
                }}
                key={row.id}
              >
                {row.getVisibleCells().map((cell) => {
                  const column = cell.column.columnDef;
                  const isEditable = rowEditable;
                  const customClassName = getCellClassName
                    ? getCellClassName(cell, row)
                    : '';
                  return (
                    <td
                      {...{
                        className: `td ${
                          highlightColumnsCell &&
                          highLightColumnsById.includes(cell.column.id)
                            ? 'highlight-cell'
                            : ''
                        } ${customClassName}`,
                        style: {
                          position: 'relative',
                          minWidth: cell.column.getSize(),
                        },
                      }}
                      key={cell.id}
                    >
                      {isEditable ? (
                        <input
                          type="text"
                          value={cell.getValue() as string}
                          onChange={(e) =>
                            handleRowEdit(
                              cell.row.index,
                              cell.column.id,
                              e.target.value,
                            )
                          }
                          className="editable-input"
                        />
                      ) : (
                        flexRender(column.cell, cell.getContext())
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {!isLoading && !table.getRowModel().rows.length
          ? emptyStateElement
          : null}
      </div>

      <TablePagination
        manualPagination={manualPagination}
        page={pagination.pageIndex + 1}
        pageSize={pagination.pageSize}
        totalEntries={
          manualPagination
            ? totalEntries
            : table.getFilteredRowModel().rows.length
        }
        isLoading={isLoading}
        setPage={(pageIndex: number) => {
          setPagination((prev) => ({ ...prev, pageIndex: pageIndex - 1 }));
          if (manualPagination && changeActions.onPaginationChange) {
            changeActions.onPaginationChange({
              ...pagination,
              pageIndex: pageIndex - 1,
            });
          }
        }}
        setPageSize={(pageSize: number) => {
          setPagination((prev) => ({ ...prev, pageIndex: 0, pageSize }));
          if (manualPagination && changeActions.onPaginationChange) {
            changeActions.onPaginationChange({
              ...pagination,
              pageIndex: 0,
              pageSize,
            });
          }
        }}
        totalRecord={table.getCoreRowModel().rows.length}
      />
    </>
  );
}
